import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import getDaysBetween from 'utils/time/getDaysBetween';
import parse from 'utils/time/parse';
import { isoDatePattern } from 'utils/time/datePatterns';
import { format } from 'utils/time/format';
import isSameDay from 'utils/time/isSameDay';
import { deserializeEvent } from 'containers/CahierJournal/queries/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';

export const EVENTS_KEY = 'events';
export const EVENTS_LIST = 'list';

interface Props {
  from: string;
  to: string;
  schoolyearId?: string;
  schoolId?: string;
}

export function getEventsBetweenDates({
  schoolyearId,
  schoolId,
  from,
  to,
  tz,
}: {
  schoolyearId: string;
  schoolId: string;
  from: string;
  to: string;
  tz: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/cahier-journal?from=${from}&to=${to}`;
  return request(requestURL, {
    method: 'GET',
  }).then(({ data }: { data: any[] }) =>
    (data || []).map(d => deserializeEvent(d, tz)),
  );
}

export function useCJSeances(props: Props) {
  const { from, to } = props;
  const { schoolyear, school } = useSchoolSchoolyear();
  const schoolyearId = props?.schoolyearId || schoolyear.id;
  const schoolId = props?.schoolId || school.id;
  const user = useUser();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [schoolyearId, schoolId, EVENTS_KEY, EVENTS_LIST, from, to],
    queryFn: () =>
      getEventsBetweenDates({
        schoolyearId,
        schoolId,
        from,
        to,
        tz: user.location,
      }).then(cjEvents => {
        const days: Date[] = getDaysBetween(
          parse(from, isoDatePattern),
          parse(to, isoDatePattern),
        );
        days.forEach(d => {
          queryClient.setQueryData(
            [schoolyearId, schoolId, EVENTS_KEY, format(d, 'fr-FR')],
            cjEvents.filter(e => isSameDay(e.start, d)),
          );
        });
        return cjEvents;
      }),
    placeholderData: [],
    refetchOnWindowFocus: false,
  });
}

export function useCJEventsLength(props: Props) {
  const { data } = useCJSeances(props);
  return (data || []).length;
}

// use a stable function reference
// see https://tanstack.com/query/latest/docs/framework/react/reference/useQueries#memoization
function combineEventCounts(results: any[]) {
  return {
    totalEvents: results
      .map(result => (result.data || []).length)
      .reduce((a, b) => a + b, 0),
  };
}

export function useCJEventsEmpty(props: Props) {
  const { from, to } = props;
  const { schoolyear, school } = useSchoolSchoolyear();
  const schoolyearId = props?.schoolyearId || schoolyear.id;
  const schoolId = props?.schoolId || school.id;

  const days: Date[] = getDaysBetween(
    parse(from, isoDatePattern),
    parse(to, isoDatePattern),
  );

  // Create a query for each day to watch its cache
  const { totalEvents } = useQueries({
    queries: days.map(d => ({
      queryKey: [schoolyearId, schoolId, EVENTS_KEY, format(d, 'fr-FR')],
      queryFn: () => Promise.resolve([]), // Don't fetch, just observe
      staleTime: Infinity,
      enabled: false, // Don't fetch
      initialData: [],
    })),
    combine: combineEventCounts,
  });

  return totalEvents === 0;
}
